/**
 * Stale-SPA / chunk hash mismatch recovery.
 *
 * The problem:
 *   Vite produces content-hashed chunk filenames (e.g. Products-CzIvmjWQ.js).
 *   When we redeploy, the hash changes and old chunks are removed from the CDN.
 *   A visitor who loaded the site BEFORE the deploy still has the old HTML
 *   cached, which references old hashes. The moment they navigate to a
 *   lazy-loaded page, the dynamic import fails with:
 *     "Failed to fetch dynamically imported module: …/Products-OldHash.js"
 *
 * The fix:
 *   1. `lazyWithRetry()` wraps React.lazy and retries the import once after
 *      a forced cache-busting reload. After one retry, if it still fails,
 *      the error propagates to ErrorBoundary (genuine bug, not a stale-SPA issue).
 *   2. We use sessionStorage to remember that we've already attempted one reload
 *      so we don't get stuck in an infinite reload loop.
 *   3. A global `vite:preloadError` listener catches the same class of failure
 *      that might slip past the React boundary (e.g. from preload hints).
 */

import { lazy, ComponentType } from "react";

const RELOAD_KEY = "voom:chunk-reload-attempts";
// Cap how many forced reloads we'll do per session. A single reload usually
// fixes the stale-SPA case, but transient CDN errors can leave a user stuck
// in a reload loop. After MAX_RELOADS we let the error surface to ErrorBoundary
// instead of looping forever.
const MAX_RELOADS = 3;
const CHUNK_ERROR_RE =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

/**
 * Returns true if the error is a chunk-loading failure (stale SPA), false
 * otherwise. Exposed so ErrorBoundary can branch on it.
 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = errorMessage(err);
  if (!msg) return false;
  return CHUNK_ERROR_RE.test(msg);
}

// Cross-engine version-skew patterns. The SAME underlying TypeError (React
// hooks dispatcher is null because two React copies are in memory after a
// redeploy) gets phrased differently by every browser engine. Missing any
// of these means iOS or older-Android-WebView users see the error UI
// instead of getting silently auto-reloaded.
//
// V8 modern (Chrome 91+, Edge, Samsung Internet, Android Chrome):
//   "Cannot read properties of null (reading 'X')"
// V8 legacy (pre-2021 Chromium, older Android WebView):
//   "Cannot read property 'X' of null"
// JSCore (iOS Safari, all versions):
//   "null is not an object (evaluating 'X')"
//   "undefined is not an object (evaluating 'X')"
//   "null is not a function" / "undefined is not a function"
// Firefox / generic:
//   "X is null" / "X is undefined" / "X is not a function" / "X is not defined"
const VERSION_SKEW_RE =
  /Cannot read propert(?:y|ies)(?:.{0,60}?) of (?:null|undefined)|(?:null|undefined) is not an object|(?:null|undefined) is not a function|is not a function|is not defined/i;

/**
 * Returns true if the error message looks like a runtime version-skew
 * symptom (TypeError shaped like a broken module graph). False for null /
 * undefined / unmatched messages.
 *
 * Same set is used by ErrorBoundary.componentDidCatch and by the early
 * window-level error listeners in main.tsx — single source of truth.
 */
export function isVersionSkewError(err: unknown): boolean {
  const msg = errorMessage(err);
  if (!msg) return false;
  return VERSION_SKEW_RE.test(msg);
}

function errorMessage(err: unknown): string {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && "message" in err) {
    const m = (err as { message: unknown }).message;
    return typeof m === "string" ? m : "";
  }
  return "";
}

/**
 * Force a full page reload with a cache-busting query param so the browser
 * fetches fresh HTML (which references the NEW chunk hashes). Tracks attempts
 * via sessionStorage and bails out after MAX_RELOADS to prevent infinite
 * reload loops when the failure is a genuine bug or a flapping CDN.
 */
export function reloadForNewVersion(): void {
  if (typeof window === "undefined") return;
  try {
    // P2: Robust parse. A corrupted or non-numeric sessionStorage value
    // (e.g. from a browser extension or storage quota hiccup) used to
    // read as NaN, and `NaN >= MAX_RELOADS` is false, which would send
    // us into an infinite reload loop writing "NaN" back. Normalize to
    // a safe non-negative integer and cap it defensively.
    const raw = sessionStorage.getItem(RELOAD_KEY) ?? "0";
    const parsed = parseInt(raw, 10);
    const prev =
      Number.isFinite(parsed) && parsed >= 0
        ? Math.min(parsed, MAX_RELOADS)
        : 0;
    if (prev >= MAX_RELOADS) return;
    sessionStorage.setItem(RELOAD_KEY, String(prev + 1));
  } catch {
    /* sessionStorage may be blocked (incognito / tracking protection) */
  }
  // Force bypass cache by appending a timestamp
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}

/**
 * Clear the reload guard. Call this on successful first render after reload
 * so a FUTURE stale-SPA scenario in the same session can trigger the same
 * recovery flow.
 */
export function clearReloadGuard(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Drop-in replacement for React.lazy() that automatically recovers from
 * stale-SPA chunk-load failures. Behaviour:
 *
 *   - First import attempt fails with a chunk-load error AND we haven't
 *     already reloaded this session → trigger a full page reload so the
 *     browser fetches fresh HTML with the new chunk hashes.
 *   - First import attempt fails with anything else → propagate the error.
 *   - We've already reloaded once → propagate the error (genuine bug).
 *
 * Usage:
 *   const Products = lazyWithRetry(() => import("./pages/Products"));
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      if (isChunkLoadError(err)) {
        // Stale SPA, reload to pick up the new manifest.
        reloadForNewVersion();
        // Keep the promise pending until the reload actually happens;
        // React will show the Suspense fallback in the meantime.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}

/**
 * Install window-level listeners that catch the version-skew and chunk-load
 * error classes BEFORE React mounts the ErrorBoundary. Three listeners:
 *
 *   1. `vite:preloadError` — Vite dispatches this on `<link rel="modulepreload">`
 *      failures. Always a stale-SPA event; reload directly.
 *   2. `error` (window) — catches synchronous errors during initial bundle
 *      execution (the high-volume "Pre-init error: Script error." Sentry class
 *      that fires before ErrorBoundary exists).
 *   3. `unhandledrejection` — catches async errors from dynamic imports /
 *      promises that don't get caught by lazyWithRetry (e.g., a dynamic import
 *      chain whose failure surfaces as a rejected promise outside React's tree).
 *
 * Listeners 2 and 3 only trigger a reload on messages that match either the
 * chunk-load or the version-skew pattern — see isChunkLoadError /
 * isVersionSkewError. Real product bugs propagate normally.
 *
 * Idempotent — calling more than once is a no-op.
 */
let globalListenerInstalled = false;
export function installStaleSpaRecovery(): void {
  if (typeof window === "undefined" || globalListenerInstalled) return;
  globalListenerInstalled = true;

  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    reloadForNewVersion();
  });

  window.addEventListener("error", (event: ErrorEvent) => {
    const err = event.error ?? event.message;
    if (isChunkLoadError(err) || isVersionSkewError(err)) {
      reloadForNewVersion();
    }
  });

  window.addEventListener(
    "unhandledrejection",
    (event: PromiseRejectionEvent) => {
      const err = event.reason;
      if (isChunkLoadError(err) || isVersionSkewError(err)) {
        reloadForNewVersion();
      }
    }
  );

  // Clear the reload guard after a successful page load so the next
  // stale-SPA scenario in the same session can auto-recover.
  if (document.readyState === "complete") {
    clearReloadGuard();
  } else {
    window.addEventListener("load", () => clearReloadGuard(), { once: true });
  }
}
